<template>
  <b-modal
    id="add-staff-category-modal"
    ref="modal"
    v-model="open"
    size="lg"
    no-close-on-backdrop
    hide-footer
    title="ADD STAFF CATEGORY TYPE"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="createStaffCategoryForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onClickCreateStaffCategoryType)"
        >
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required staff-category-label"
                label="Staff Type Name"
                label-for="staff-type-name"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="Staff Type Name"
                  rules="required"
                >
                  <b-form-input
                    id="staff-type-name"
                    v-model="form.name"
                    placeholder="Enter Staff Type Name"
                    type="text"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required staff-category-label"
                label="ID Prefix"
                label-for="id-prefix"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="ID Prefix"
                  rules="required"
                >
                  <b-form-input
                    id="id-prefix"
                    v-model="form.id_prefix"
                    placeholder="Enter ID Prefix"
                    type="text"
                    :state="errors.length > 0 ? false : null"
                    @input="updatePreview"
                    @keydown.space.prevent
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required staff-category-label"
                label="Digit Length"
                label-for="digit-length"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="Digit Length"
                  rules="required|numeric|min_value:1"
                >
                  <b-form-input
                    id="digit-length"
                    v-model="form.digit_length"
                    placeholder="Enter Digit Length"
                    type="number"
                    min="1"
                    :state="errors.length > 0 ? false : null"
                    @input="updatePreview"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required staff-category-label"
                label="Starting Number"
                label-for="starting-number"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="Starting Number"
                  rules="required|numeric|min_value:1"
                >
                  <b-form-input
                    id="starting-number"
                    v-model="form.starting_number"
                    placeholder="Enter Starting Number"
                    type="number"
                    min="1"
                    :state="errors.length > 0 ? false : null"
                    @input="updatePreview"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="staff-category-label"
                label="Preview"
                label-for="preview"
              >
                <b-form-input
                  id="preview"
                  v-model="preview"
                  placeholder="Preview"
                  readonly
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12 mb-1">
              <div class="d-flex justify-content-end">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  variant="primary"
                  class="mr-1"
                >
                  Submit
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="reset"
                  variant="outline-secondary"
                  @click="resetForm"
                >
                  Clear
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, numeric, min_value } from 'vee-validate/dist/rules'
import {
  BButton,
  BModal,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BRow,
  BOverlay,
  VBModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

extend('required', required)
extend('numeric', numeric)
extend('min_value', min_value)

const StaffSettingsRepository = RepositoryFactory.get('staffSetting')

function initialState() {
  return {
    open: false,
    modalLoading: false,
    form: {
      name: '',
      id_prefix: '',
      starting_number: '',
      digit_length: '',
    },
    preview: '',
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BRow,
    BOverlay,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  methods: {
    updatePreview() {
      const prefix = this.form.id_prefix || ''
      const startingNumber = Number(this.form.starting_number)
      const digitLength = Number(this.form.digit_length)

      if (!prefix || !startingNumber || !digitLength) {
        this.preview = ''
        return
      }

      this.preview = `${prefix}-${String(startingNumber).padStart(digitLength, '0')}`
    },
    resetForm() {
      Object.assign(this.$data, {
        ...initialState(),
        open: this.open,
      })
    },
    openModal() {
      Object.assign(this.$data, initialState())
      this.open = true
    },
    async onClickCreateStaffCategoryType() {
      this.modalLoading = true
      try {
        const digitLength = Number(this.form.digit_length)
        const startingNumber = Number(this.form.starting_number)
        const payload = {
          name: this.form.name,
          prefix: this.form.id_prefix,
          prefix_length: this.form.id_prefix.length,
          start_from: String(startingNumber).padStart(digitLength, '0'),
          number_of_digits: digitLength,
          preview: this.preview,
        }
        await StaffSettingsRepository.createStaffCategoryType(payload)
        this.showSuccessMessage('Staff category type created successfully')
        this.open = false
        this.$parent.getStaffCategoryTypesList()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.modalLoading = false
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/required.scss';

.staff-category-label label {
  font-size: 1rem !important;
}
</style>
